import { useCallback, useEffect, useMemo, useState } from "react";

const ACTOR_STAGES = [
  "arti.chat.v1",
  "arti.chat.v1.cache_warm",
  "arti.chat.v1.consolidation",
  "arti.chat.v1.compact_seed",
] as const;

const LOOKBACK_DAYS = 120;

interface UsageTotals {
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  cachedInputTokens: number;
  cacheWriteTokens: number;
  costUsd?: number;
  costBreakdown?: { totalUsd?: number };
}

interface UsageBucket {
  startTimestampMs: number;
  endTimestampMs: number;
  totals: UsageTotals;
}

interface UsageSummaryResponse {
  totals: UsageTotals;
  window: { fromTimestampMs: number; toTimestampMs: number };
  timeseries?: { bucketUnit: "hour" | "day" | null; buckets: UsageBucket[] };
  breakdowns: {
    byStage: { stage: string; totals: UsageTotals }[];
    byModel: { model: string | null; totals: UsageTotals }[];
  } | null;
}

export interface DailyBucket {
  dayStartMs: number;
  usd: number;
  tokens: number;
}

export interface StageRow {
  stage: string;
  usd: number;
  tokens: number;
}

export interface ModelRow {
  model: string;
  usd: number;
  tokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
}

export interface ActorSpendHistory {
  totalUsd: number;
  totalTokens: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheWriteTokens: number;
  entryCount: number;
  activeDays: number;
  firstBucketMs: number | null;
  lastBucketMs: number | null;
  byDay: DailyBucket[];
  byStage: StageRow[];
  byModel: ModelRow[];
}

interface State {
  data: ActorSpendHistory | null;
  loading: boolean;
  error: string | null;
}

function costOf(t: UsageTotals | undefined): number {
  if (!t) {
    return 0;
  }
  return t.costBreakdown?.totalUsd ?? t.costUsd ?? 0;
}

function mergeResponses(responses: UsageSummaryResponse[]): ActorSpendHistory {
  let totalUsd = 0;
  let totalTokens = 0;
  let inputTokens = 0;
  let outputTokens = 0;
  let cacheReadTokens = 0;
  let cacheWriteTokens = 0;

  const dayMap = new Map<number, DailyBucket>();
  const stageRows: StageRow[] = [];
  const modelMap = new Map<string, ModelRow>();

  for (const resp of responses) {
    totalUsd += costOf(resp.totals);
    totalTokens += resp.totals.totalTokens;
    inputTokens += resp.totals.promptTokens;
    outputTokens += resp.totals.completionTokens;
    cacheReadTokens += resp.totals.cachedInputTokens;
    cacheWriteTokens += resp.totals.cacheWriteTokens;

    for (const bucket of resp.timeseries?.buckets ?? []) {
      const dayStartMs =
        Math.floor(bucket.startTimestampMs / 86_400_000) * 86_400_000;
      const existing = dayMap.get(dayStartMs) ?? {
        dayStartMs,
        tokens: 0,
        usd: 0,
      };
      existing.usd += costOf(bucket.totals);
      existing.tokens += bucket.totals.totalTokens;
      dayMap.set(dayStartMs, existing);
    }

    for (const row of resp.breakdowns?.byStage ?? []) {
      stageRows.push({
        stage: row.stage,
        tokens: row.totals.totalTokens,
        usd: costOf(row.totals),
      });
    }

    for (const row of resp.breakdowns?.byModel ?? []) {
      const key = row.model ?? "(unknown)";
      const existing = modelMap.get(key) ?? {
        cacheReadTokens: 0,
        cacheWriteTokens: 0,
        model: key,
        tokens: 0,
        usd: 0,
      };
      existing.usd += costOf(row.totals);
      existing.tokens += row.totals.totalTokens;
      existing.cacheReadTokens += row.totals.cachedInputTokens;
      existing.cacheWriteTokens += row.totals.cacheWriteTokens;
      modelMap.set(key, existing);
    }
  }

  const byDay = [...dayMap.values()]
    .filter((d) => d.usd > 0 || d.tokens > 0)
    .toSorted((a, b) => a.dayStartMs - b.dayStartMs);
  const byStage = stageRows
    .filter((r) => r.usd > 0 || r.tokens > 0)
    .toSorted((a, b) => b.usd - a.usd);
  const byModel = [...modelMap.values()]
    .filter((m) => m.usd > 0 || m.tokens > 0)
    .toSorted((a, b) => b.usd - a.usd);

  const firstBucketMs = byDay[0]?.dayStartMs ?? null;
  const lastBucketMs = byDay[byDay.length - 1]?.dayStartMs ?? null;

  return {
    activeDays: byDay.length,
    byDay,
    byModel,
    byStage,
    cacheReadTokens,
    cacheWriteTokens,
    entryCount: 0,
    firstBucketMs,
    inputTokens,
    lastBucketMs,
    outputTokens,
    totalTokens,
    totalUsd,
  };
}

export function useActorSpendHistory(enabled: boolean): State {
  const [state, setState] = useState<State>({
    data: null,
    error: null,
    loading: false,
  });

  const range = useMemo(() => {
    const to = Date.now();
    const from = to - LOOKBACK_DAYS * 86_400_000;
    return { from, to };
  }, []);

  const fetchAll = useCallback(async () => {
    setState({ data: null, error: null, loading: true });
    try {
      const responses = await Promise.all(
        ACTOR_STAGES.map(async (stage) => {
          const params = new URLSearchParams({
            bucketUnit: "day",
            from: String(range.from),
            includeBreakdowns: "true",
            includeTimeseries: "true",
            stage,
            to: String(range.to),
          });
          const res = await fetch(`/user/usage/summary?${params.toString()}`);
          if (!res.ok) {
            throw new Error(`HTTP ${res.status} fetching stage '${stage}'`);
          }
          // oxlint-disable-next-line typescript/no-unsafe-type-assertion
          return (await res.json()) as UsageSummaryResponse;
        }),
      );
      setState({
        data: mergeResponses(responses),
        error: null,
        loading: false,
      });
    } catch (error) {
      setState({
        data: null,
        error:
          error instanceof Error
            ? error.message
            : "Failed to load spend history",
        loading: false,
      });
    }
  }, [range.from, range.to]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void fetchAll();
  }, [enabled, fetchAll]);

  return state;
}
